import React, {useMemo, useState, useEffect, useRef} from "react"
import {default as ReactPictureMosaic} from "react-picture-mosaic";
import {preloadImages, useImage, useMosaicImages} from "./use_mosaic_images";
import {orderBy, merge} from "lodash"
import moment from "moment";
import {drawImageToCanvasWithCover, mergeAndSum} from "./utils";
import RNG from "./rng";
import {PictureMosaicLoading} from "./picture_mosaic_loading";
import {usePossibleGridTargets} from "./use_logo_picture_mosaic";

/**
 * @param {Object} props - The props object.
 *
 * @param {string} props.imageUrl Url of the image to be used as the mosaic.
 * @param {number} props.gridWidth Number of columns in the mosaic.
 * @param {number} props.gridHeight Number of rows in the mosaic.
 * @param {number} props.imageInterval Time in milliseconds between each image change.
 * @param {'media_files'|'tag_owner_images'|'stored_photos'} props.pictureMode Mode for selecting images.
 * @param {string|undefined} props.blockingTileColor Color of the tiles that are blocking not revealed parts of the mosaic.
 * @param {number} props.seed Seed for the random number generator.
 * @param {number | undefined} props.minBackgroundThreshold Min. threshold for the logo mode when parts of the background are considered as part of the logo.
 * @param {number | undefined} props.maxBackgroundThreshold Max. threshold for the logo mode when parts of the background are considered as part of the logo.
 * @param {boolean} props.onlyApprovedImages
 * @param {'default'|'logo'} props.mosaicMode
 * @param {numer) props.overlayOpacity Opacity of the overlay.
 * @param {numer) props.canvasOpacity Opacity of the canvas (tiles).
 * @param {"string") props.overlayMixBlendMode Blend mode of the mix tile must be a valid css mix-blend-mode.
 * @param {"string") props.imageIdentifier Identifier by which the images are filtered, if omitted all images are used.
 * @returns {JSX.Element} React component.
 */
export const PictureMosaic = (
  {
    imageUrl,
    gridWidth,
    gridHeight,
    imageInterval,
    pictureMode = 'stored_photos',
    blockingTileColor,
    seed = 71443,
    minBackgroundThreshold = 252,
    maxBackgroundThreshold = 252,
    onlyApprovedImages = false,
    mosaicMode = "default",
    overlayOpacity = 0.8,
    canvasOpacity = 1,
    overlayMixBlendMode = "unset",
    imageIdentifier,
  }
) => {
  const [seedPercentage, setSeedPercentage] = useState(0)
  const image = useImage(imageUrl)

  useEffect(() => {
    const listener = (event) => {
      const allowedKeys = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]
      if (!allowedKeys.includes(event.key)) {
        return
      }
      setSeedPercentage(parseInt(event.key) * (100 / 8))
    }

    document.addEventListener("keydown", listener)
    return () => {
      document.removeEventListener("keydown", listener)
    }
  }, [])

  const aspectRatio = `${image?.naturalWidth || 16}/${image?.naturalHeight || 9}`
  const style = {
    width: "100%",
    height: "auto",
    aspectRatio: aspectRatio,
    position: "relative",
  }

  return <div className={"picture-mosaic-container"} style={style}>
    <RenderedPictureMosaic
      key={seedPercentage} seedPercentage={seedPercentage}
      imageUrl={imageUrl}
      gridWidth={gridWidth}
      gridHeight={gridHeight}
      imageInterval={imageInterval}
      pictureMode={pictureMode}
      blockingTileColor={blockingTileColor}
      seed={seed}
      minBackgroundThreshold={minBackgroundThreshold}
      maxBackgroundThreshold={maxBackgroundThreshold}
      mosaicMode={mosaicMode}
      overlayOpacity={overlayOpacity}
      canvasOpacity={canvasOpacity}
      overlayMixBlendMode={overlayMixBlendMode}
      imageIdentifier={imageIdentifier}
      onlyApprovedImages={onlyApprovedImages}
    />
  </div>
}

const RenderedPictureMosaic = (
  {
    imageUrl,
    gridWidth,
    gridHeight,
    imageInterval,
    pictureMode = 'stored_photos',
    seedPercentage = 0,
    blockingTileColor,
    seed = 71443,
    minBackgroundThreshold,
    maxBackgroundThreshold,
    mosaicMode = "default",
    overlayOpacity,
    canvasOpacity,
    overlayMixBlendMode,
    imageIdentifier,
    onlyApprovedImages,
  }
) => {
  const rng = useMemo(() => {
    return new RNG(seed)
  }, [seed])

  const loadTileImage = useRef(null)
  const nextTileTarget = useRef(null)

  const [seedImagesLoaded, setSeedImagesLoaded] = useState(true)
  const [usedSeedImages, setUsedSeedImages] = useState({})
  const [usedImages, setUsedImages] = useState({})
  const [currentTargetIndex, setCurrentTargetIndex] = useState(0)
  const [preloadProgress, setPreloadProgress] = useState(0)

  const possibleGridTargets = usePossibleGridTargets({
    imageUrl,
    minBackgroundThreshold,
    maxBackgroundThreshold,
    gridWidth,
    gridHeight,
  })

  const targetList = useMemo(() => {
    if (mosaicMode === "logo") {
      return rng.shuffle(possibleGridTargets || [])
    }
    const targetList = []
    for (let j = 0; j < gridHeight; j++) {
      for (let i = 0; i < gridWidth; i++) {
        targetList.push({column: i, row: j})
      }
    }
    return rng.shuffle(targetList)
  }, [rng, gridWidth, gridHeight, mosaicMode, possibleGridTargets])

  const seedImageSize = 'xs'

  const unfilteredImages = useMosaicImages(pictureMode)
  const images = useMemo(() => {
    let targetedImages = unfilteredImages

    if (targetedImages) {
      targetedImages = rng.shuffle(targetedImages)
    }

    if (onlyApprovedImages) {
      targetedImages = targetedImages?.filter(image => !!image.approved_at)
    }

    if (!imageIdentifier || !targetedImages) {
      return targetedImages
    }

    return targetedImages?.filter(image => image.identifier === imageIdentifier)
  }, [unfilteredImages, imageIdentifier, onlyApprovedImages])

  const seedImages = useMemo(() => {
    if (seedPercentage == 0 || !images) {
      return []
    }

    const seedImages = []
    const lastSeedIndex = Math.ceil(targetList.length * (seedPercentage / 100))
    const partialTargetTiles = targetList.slice(0, lastSeedIndex)
    setCurrentTargetIndex(lastSeedIndex)

    for (let j = 0; j < gridHeight; j++) {
      for (let i = 0; i < gridWidth; i++) {
        const randomImage = images[Math.floor(rng.nextFloat() * images.length)]
        const shouldBeSeeded = partialTargetTiles.find(tile => tile.column === i && tile.row === j)
        if (shouldBeSeeded) {
          seedImages.push(randomImage.urls[seedImageSize])
        } else {
          seedImages.push(null)
        }
      }
    }

    return seedImages
  }, [seedPercentage, gridWidth, gridHeight, images?.length > 0, targetList.length > 0])

  const seedLoaded = (seedPercentage <= 0 || (targetList.length > 0 && seedImages && seedImages.length > 0))
  const isReady = images && images.length > 0 && seedImagesLoaded && seedLoaded

  useEffect(() => {
    if (seedImages && seedImages.length > 0) {
      setSeedImagesLoaded(false)
      preloadImages(seedImages.filter(seedImage => !!seedImage), 5, setPreloadProgress)
        .then(() => {
          setSeedImagesLoaded(true)
        }).catch((error) => {
        console.error(error)
        alert(error)
      })
    }
  }, [seedImages && seedImages.length > 0])


  useEffect(() => {
    if (isReady) {
      setUsedSeedImages(seedImages.filter(seedImage => !!seedImage).reduce(function (counts, str) {
        const imageId = images.find(image => image.urls[seedImageSize] === str).id
        counts[imageId] = moment()
        return counts;
      }, {}))
    }
  }, [isReady])

  const containerStyle = {
    position: "absolute",
    top: "0",
    left: "0",
    width: `100%`, //${targetWidth}px`,
    height: `100%`, //${targetHeight}px`
  }

  useEffect(() => {
    nextTileTarget.current = (previousColumn, previousRow, config) => {
      let currentTarget = targetList[currentTargetIndex % targetList.length]
      // If we already are through all the targets we need to shuffle them, otherwise we will later create a pattern?
      if (currentTargetIndex > targetList.length) {
        currentTarget = targetList[Math.floor(rng.nextFloat() * targetList.length)]
      }
      setCurrentTargetIndex(currentTargetIndex => currentTargetIndex + 1)
      return currentTarget
    }
  }, [currentTargetIndex, targetList])


  useEffect(() => {
    loadTileImage.current = (column, row) => {
      const imageUsages = merge(usedSeedImages || {}, usedImages || {})
      const newTarget = orderBy(images, [
        item => imageUsages[item.id] || moment("1900-09-09"),
        item => moment(item.created_at),
      ], ["asc", "desc"])[0]

      if (newTarget) {
        setUsedImages(useImages => {
          useImages ||= {}
          const newUsedSeedImages = {...useImages}
          newUsedSeedImages[newTarget.id] = moment()
          return newUsedSeedImages
        })
      }
      return newTarget.url
    }
  }, [images, setUsedImages, usedImages, usedSeedImages]);

  const pictureMosaic = useMemo(() => {
    const actualNextTileTarget = (previousColumn, previousRow, config) => {
      return nextTileTarget.current(previousColumn, previousRow, config)
    }
    const actualLoadTileImage = (column, row) => {
      return loadTileImage.current(column, row)
    }
    const drawCanvasTileToCanvas = (canvas, image, width, height, x, y) => {
      if (image.image) {
        const context = canvas.getContext('2d');
        context.clearRect(x, y, width, height);
        const img = new Image()
        img.onload = () => {
          drawImageToCanvasWithCover(context, img, x, y, width, height)
        }
        img.src = image.image
      }
    }

    const blockingTileStyle = {
      backgroundColor: blockingTileColor || "white",
    }

    // Could possible set the z index of overlay and canvas to reorder them if needed
    const overlayStyle = {
      mixBlendMode: overlayMixBlendMode || (mosaicMode === "default" ? "unset" : "darken"),
      opacity: overlayOpacity,
    }

    const canvasStyle = {
      opacity: canvasOpacity,
    }

    const createTileAnimations = (column, row, element, gridRect, config) => {
      const width = Math.round((gridRect?.width || 1) / config.columns)
      const height = Math.round((gridRect?.height || 1) / config.rows)
      const translateX = column * width
      const translateY = row * height

      const previewWidth = gridRect.width / 2
      const previewHeight = gridRect.height / 2
      const previewTranslateX = gridRect.width / 2 - (previewWidth / 2)
      const previewTranslateY = gridRect.height / 2 - (previewHeight / 2)

      return [
        {
          delay: 0,
          config: {
            duration: 0,
            easing: (t) => ((t *= 2) <= 1 ? t * t : --t * (2 - t) + 1) / 2
          },
          immediate: true,
          zIndex: 600,
          opacity: 1,
          width: `0px`,
          height: `0px`,
          transform: `translate(${previewTranslateX + previewWidth/2}px, ${previewTranslateY + previewHeight/2}px)`,
        },
        {
          delay: 1,
          config: {
            duration: 750,
          },
          zIndex: 500,
          opacity: 1,
          width: `${previewWidth}px`,
          height: `${previewHeight}px`,
          transform: `translate(${previewTranslateX}px, ${previewTranslateY}px)`,
        },
        {
          delay: 2000,
          config: {
            duration: 2000,
            easing: (t) => ((t *= 2) <= 1 ? t * t : --t * (2 - t) + 1) / 2
          },
          zIndex: 400,
          opacity: 0.5,
          width: `${width}px`,
          height: `${height}px`,
          transform: `translate(${translateX}px, ${translateY}px)`,
          transformOrigin: "center"
        }
      ]
    }


    return <ReactPictureMosaic
      columns={gridWidth}
      rows={gridHeight}
      mosaicImage={imageUrl}
      imageSeed={seedImages}
      overlayStyle={overlayStyle}
      canvasStyle={canvasStyle}
      createTileAnimations={createTileAnimations}
      blockingTileStyle={blockingTileStyle}
      loadTileImage={actualLoadTileImage}
      nextTileTarget={actualNextTileTarget}
      drawTileToCanvas={drawCanvasTileToCanvas}
      imageInterval={Number(imageInterval)}
    />
  }, [
    seedImages,
    gridWidth,
    gridHeight,
    imageUrl,
    imageInterval,
  ])

  if (!isReady) {
    return <PictureMosaicLoading preloadProgress={preloadProgress} />
  }

  return <div style={containerStyle}>
    <style>
      {`.animated-tile {background-size: contain !important;`}
    </style>
    {pictureMosaic}
  </div>
}
